import type { ReactNode } from 'react'
import { Badge, Flex, Select, Text } from '@radix-ui/themes'
import type { TRPCOutputs } from '~/utils/trpc'

export interface OrgSummaryCounts {
  installations: number
  stalled: number
  openTickets: number
  // Null while their own queries are still in flight, so nothing reports a confident zero.
  stations: number | null
  contacts: number | null
}

type Org = TRPCOutputs['ops']['orgs']['list'][number]

interface OrgHeaderProps {
  orgs: Org[]
  orgId: string | undefined
  loading: boolean
  counts: OrgSummaryCounts | null
  actions: ReactNode
  onOrgChange: (orgId: string) => void
  onStalledClick: () => void
}

const Separator = () => (
  <Text size="2" color="gray">
    ·
  </Text>
)

export const OrgHeader = ({
  orgs,
  orgId,
  loading,
  counts,
  actions,
  onOrgChange,
  onStalledClick
}: OrgHeaderProps) => {
  const selected = orgs.find((org) => org.id === orgId) ?? null

  return (
    <Flex direction="column" gap="2">
      <Flex align="center" justify="between" gap="3" wrap="wrap">
        <Flex direction="column" gap="1">
          {orgId && (
            <Text size="1" color="gray" weight="medium" className="uppercase tracking-wider">
              Organizations
            </Text>
          )}
          <Select.Root value={orgId ?? ''} onValueChange={onOrgChange} disabled={loading}>
            {orgId ? (
              <Select.Trigger variant="ghost" aria-label="Switch organization">
                <Text size="6" weight="bold">
                  {selected?.name ?? 'Select an organization…'}
                </Text>
              </Select.Trigger>
            ) : (
              <Select.Trigger variant="soft" placeholder="Pick organization" />
            )}
            <Select.Content>
              {orgs.map((org) => (
                <Select.Item key={org.id} value={org.id}>
                  {org.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
        {actions}
      </Flex>

      {counts && (
        <Flex align="center" gap="2" wrap="wrap">
          <Text size="2" color="gray">
            {counts.installations} installation{counts.installations === 1 ? '' : 's'}
          </Text>
          {counts.stalled > 0 && (
            <>
              <Separator />
              <Badge color="red" variant="soft" size="1" className="cursor-pointer" onClick={onStalledClick}>
                ⚠ {counts.stalled} stalled
              </Badge>
            </>
          )}
          {counts.openTickets > 0 && (
            <>
              <Separator />
              <Badge color="orange" variant="soft" size="1">
                🎫 {counts.openTickets} open
              </Badge>
            </>
          )}
          {counts.stations !== null && (
            <>
              <Separator />
              <Text size="2" color="gray">
                {counts.stations} station{counts.stations === 1 ? '' : 's'}
              </Text>
            </>
          )}
          {counts.contacts !== null && (
            <>
              <Separator />
              <Text size="2" color="gray">
                {counts.contacts} contact{counts.contacts === 1 ? '' : 's'}
              </Text>
            </>
          )}
        </Flex>
      )}
    </Flex>
  )
}
